// Global authentication utility functions
window.GrowerAuth = {
  // Check if grower is authenticated
  isAuthenticated() {
    const sessionToken = sessionStorage.getItem('growerToken');
    return !!sessionToken;
  },

  // Get grower token from storage
  getToken() {
    const sessionToken = sessionStorage.getItem('growerToken');
    try {
      return JSON.parse(sessionToken || '{}');
    } catch (error) {
      console.log('Error parsing token:', error);
      return {};
    }
  },

  // Redirect based on authentication status
  checkAuthAndRedirect(requireAuth = false) {
    const isAuth = this.isAuthenticated();
    const currentPath = window.location.pathname;
    
    if (requireAuth && !isAuth) {
      // Require authentication but user is not logged in
      if (!currentPath.includes('grower-login')) {
        window.location.replace('/pages/grower-login');
        return false;
      }
    } else if (!requireAuth && isAuth) {
      // Don't require authentication but user is logged in
      if (!currentPath.includes('grower-account-page')) {
        console.log('Redirecting to dashboard (already logged in)');
        window.location.href = '/pages/grower-account-page';
        return false;
      }
    }
    
    return true; // No redirect needed
  },

  // Clear authentication data
  logout() {
    sessionStorage.removeItem('growerToken');
    // Clear hash and redirect to clean login page
    window.location.replace('/pages/grower-login');
  }
};

class GrowerAccount {
  constructor() {
    this.growerToken = null;
    this.agreementModal = null;
    this.agreementCheckbox = null;
    this.acceptAgreementBtn = null;
    this.closeAgreementBtn = null;
    this.menuLinks = null;
    this.tabContents = null;
    this.allProducts = []; // Store all products for filtering
    this.filteredProducts = []; // Store filtered products
    
    this.init();
  }

  init() {
    document.addEventListener('DOMContentLoaded', () => {
      // Check authentication status - redirect if not logged in
      const sessionToken = sessionStorage.getItem('growerToken');
      
      // Avoid infinite redirect loop by checking current page
      const currentPath = window.location.pathname;
      
      if (!sessionToken) {
        // Only redirect if not already on login page
        if (!currentPath.includes('grower-login')) {
          window.location.replace('/pages/grower-login');
          return;
        }
      } else {
        // Token exists, verify it's valid
        try {
          const tokenData = JSON.parse(sessionToken);
          if (!tokenData.token || !tokenData.grower) {
            sessionStorage.removeItem('growerToken');
            if (!currentPath.includes('grower-login')) {
              window.location.replace('/pages/grower-login');
              return;
            }
          }
        } catch (error) {
          sessionStorage.removeItem('growerToken');
          if (!currentPath.includes('grower-login')) {
            window.location.replace('/pages/grower-login');
            return;
          }
        }
      }
      
      this.initializeElements();
      this.loadGrowerData();
      this.setupTabNavigation();
      this.setupAgreementModal();
      this.setupProductManagement();
      this.setupFilteringAndSorting();
    });
  }

  initializeElements() {
    // Initialize agreement modal elements
    this.agreementModal = document.getElementById('agreementModal');
    this.agreementCheckbox = document.getElementById('agreementCheckbox');
    this.acceptAgreementBtn = document.getElementById('acceptAgreement');
    this.closeAgreementBtn = document.getElementById('clearSig');
    this.menuLinks = document.querySelectorAll('.js-menu-link');
    this.tabContents = document.querySelectorAll('.js-tab-content');
    this.setupLogoutButton();
  }

  async loadGrowerData() {
    const token = sessionStorage.getItem("growerToken");
    
    if (token) {
      try {
        this.growerToken = JSON.parse(token);
        
        console.log('Loaded grower token:', this.growerToken);
        
        if (this.growerToken && this.growerToken.grower) {
          this.updateDashboardInfo();
          this.updateProfileInfo();
          this.updateWelcomeMessage();
          this.loadChecklists();
          
          if (this.growerToken.token) {
            await this.refreshProfileData();
          }
        }
      } catch (error) {
        console.error('Error loading grower data:', error);
        this.growerToken = {};
      }
    } else {
      this.growerToken = {};
    }
  }

  updateDashboardInfo() {
    const growerNameElement = document.querySelector('.account-info-item.name .account-info-value');
    if (growerNameElement) {
      growerNameElement.textContent = this.growerToken.grower.name || 'Not Set';
    }

    // Update grower email in dashboard
    const growerEmailElement = document.querySelector('.account-info-item.email .account-info-value');
    if (growerEmailElement) {
      growerEmailElement.textContent = this.growerToken.grower.email || 'Not Set';
    }

    // Update grower status in dashboard
    const statusElement = document.querySelector('.account-info-item.status .status-tag');
    if (statusElement) {
      statusElement.textContent = this.growerToken.grower.status || 'Active';
      statusElement.className = 'status-tag ' + (this.growerToken.grower.status?.toLowerCase() || 'active');
    }
  }

  updateProfileInfo() {
    const grower = this.growerToken.grower;
    
    const profileNameElement = document.querySelector('.js-tab-content[data-content="profile"] .account-info-item.name .account-info-value');
    if (profileNameElement) {
      profileNameElement.textContent = grower.name || '';
    }

    const profileEmailElement = document.querySelector('.js-tab-content[data-content="profile"] .account-info-item.email .account-info-value');
    if (profileEmailElement) {
      profileEmailElement.textContent = grower.email || grower.contactInfo?.email || '';
    }

    const profilePhoneElement = document.querySelector('.js-tab-content[data-content="profile"] .account-info-item.phone .account-info-value');
    if (profilePhoneElement) {
      profilePhoneElement.textContent = grower.phone || grower.contactInfo?.phone || '';
    }

    const profileDescriptionElement = document.querySelector('.js-tab-content[data-content="profile"] .account-info-item.description .account-info-value');
    if (profileDescriptionElement) {
      profileDescriptionElement.textContent = grower.description || '';
    }

    const addressStreetElement = document.querySelector('.js-tab-content[data-content="profile"] .account-info-item.address-street .account-info-value');
    if (addressStreetElement) {
      addressStreetElement.textContent = grower.address?.street || '';
    }

    const addressCityElement = document.querySelector('.js-tab-content[data-content="profile"] .account-info-item.address-city .account-info-value');
    if (addressCityElement) {
      addressCityElement.textContent = grower.address?.city || '';
    }

    const addressStateElement = document.querySelector('.js-tab-content[data-content="profile"] .account-info-item.address-state .account-info-value');
    if (addressStateElement) {
      addressStateElement.textContent = grower.address?.state || '';
    }

    const addressZipElement = document.querySelector('.js-tab-content[data-content="profile"] .account-info-item.address-zipCode .account-info-value');
    if (addressZipElement) {
      addressZipElement.textContent = grower.address?.zipCode || '';
    }

    const addressCountryElement = document.querySelector('.js-tab-content[data-content="profile"] .account-info-item.address-country .account-info-value');
    if (addressCountryElement) {
      addressCountryElement.textContent = grower.address?.country || '';
    }

    const businessNameElement = document.querySelector('.js-tab-content[data-content="profile"] .account-info-item.account-businessName .account-info-value');
    if (businessNameElement) {
      businessNameElement.textContent = grower.accountDetails?.businessName || '';
    }

    const bankNameElement = document.querySelector('.js-tab-content[data-content="profile"] .account-info-item.account-bankName .account-info-value');
    if (bankNameElement) {
      bankNameElement.textContent = grower.accountDetails?.bankName || '';
    }

    const accountHolderNameElement = document.querySelector('.js-tab-content[data-content="profile"] .account-info-item.account-accountHolderName .account-info-value');
    if (accountHolderNameElement) {
      accountHolderNameElement.textContent = grower.accountDetails?.accountHolderName || '';
    }

    const accountNumberElement = document.querySelector('.js-tab-content[data-content="profile"] .account-info-item.account-accountNumber .account-info-value');
    if (accountNumberElement) {
      accountNumberElement.textContent = grower.accountDetails?.accountNumber || '';
    }

    const routingNumberElement = document.querySelector('.js-tab-content[data-content="profile"] .account-info-item.account-routingNumber .account-info-value');
    if (routingNumberElement) {
      routingNumberElement.textContent = grower.accountDetails?.routingNumber || '';
    }

    const accountTypeElement = document.querySelector('.js-tab-content[data-content="profile"] .account-info-item.account-accountType .account-info-value');
    if (accountTypeElement) {
      accountTypeElement.textContent = grower.accountDetails?.accountType ? grower.accountDetails.accountType.charAt(0).toUpperCase() + grower.accountDetails.accountType.slice(1) : '';
    }

    const taxIdElement = document.querySelector('.js-tab-content[data-content="profile"] .account-info-item.account-taxId .account-info-value');
    if (taxIdElement) {
      taxIdElement.textContent = grower.accountDetails?.taxId || '';
    }

    const businessRegistrationNumberElement = document.querySelector('.js-tab-content[data-content="profile"] .account-info-item.account-businessRegistrationNumber .account-info-value');
    if (businessRegistrationNumberElement) {
      businessRegistrationNumberElement.textContent = grower.accountDetails?.businessRegistrationNumber || '';
    }
  }

  updateWelcomeMessage() {
    const welcomeElement = document.querySelector('.account-welcome');
    if (welcomeElement && this.growerToken.grower.name) {
      welcomeElement.textContent = `Welcome back, ${this.growerToken.grower.name}!`;
    }
  }

  setupTabNavigation() {
    // Only set up tab navigation if we're on the account page
    if (!window.location.pathname.includes('grower-account-page')) {
      return;
    }
    
    // Handle menu clicks
    this.menuLinks.forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const tabName = link.getAttribute('data-tab');
        this.showTab(tabName);
      });
    });

    // Check for hash in URL
    if (window.location.hash) {
      const tabName = window.location.hash.substring(1);
      this.showTab(tabName, false); // Don't update hash if it's already set
    } else {
      // Default to dashboard if no hash
      this.showTab('dashboard');
    }
  }

  showTab(tabName, updateHash = true) {
    // Update active tab in menu
    this.menuLinks.forEach((link) => {
      if (link.getAttribute('data-tab') === tabName) {
        link.classList.add('active');
      } else {
        link.classList.remove('active');
      }
    });

    // Show tab content
    this.tabContents.forEach((content) => {
      if (content.getAttribute('data-content') === tabName) {
        content.classList.remove('hidden');
      } else {
        content.classList.add('hidden');
      }
    });

    // Only update URL hash if explicitly requested and we're on the right page
    if (updateHash && window.location.pathname.includes('grower-account-page')) {
      window.location.hash = tabName;
    }
  }

  setupLogoutButton() {
    const logoutBtn = document.getElementById('grower-logout-btn');
    if (logoutBtn) {
      logoutBtn.addEventListener('click', (e) => {
        e.preventDefault();
        window.GrowerAuth.logout();
      });
    }
  }

  setupAgreementModal() {
    // Only enable submit if checkbox is checked
    if (this.agreementCheckbox && this.acceptAgreementBtn) {
      this.agreementCheckbox.addEventListener('change', () => {
        this.acceptAgreementBtn.disabled = !this.agreementCheckbox.checked;
      });
    }

    // Close modal on cancel
    if (this.closeAgreementBtn) {
      this.closeAgreementBtn.addEventListener('click', () => {
        this.hideAgreementModal();
      });
    }

    // Also close on X
    const modalCloseX = this.agreementModal?.querySelector('.modal-close');
    if (modalCloseX) {
      modalCloseX.addEventListener('click', () => this.hideAgreementModal());
    }

    // Check localStorage for growerAgreement
    if (this.growerToken && this.growerToken.grower) {
      if (!this.growerToken.grower.agreementSigned || this.growerToken.grower.agreementSigned === "false") {
        this.showAgreementModal();
      }
    }

    // On submit, call API and update localStorage
    if (this.acceptAgreementBtn) {
      this.acceptAgreementBtn.addEventListener('click', () => this.handleAgreementSubmit());
    }
  }

  showAgreementModal() {
    if (this.agreementModal) {
      this.agreementModal.style.display = 'block';
    }
  }

  hideAgreementModal() {
    if (this.agreementModal) {
      this.agreementModal.style.display = 'none';
    }
  }

  async handleAgreementSubmit() {
    if (!this.agreementCheckbox.checked) {
      return;
    }

    try {
      // Get grower ID from token
      const growerId = this.growerToken.grower.id || this.growerToken.grower._id;
      
      if (!growerId) {
        throw new Error('Grower ID not found');
      }

      const response = await fetch(`https://growers-bk.vercel.app/api/growers/${growerId}/agreement`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.growerToken.token}`
        },
        body: JSON.stringify({
          agreementSigned: true
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || 'Failed to submit agreement');
      }

      const result = await response.json();
      
      // Update sessionStorage with the response data
      if (result.grower) {
        this.growerToken.grower.agreementSigned = result.grower.agreementSigned;
        sessionStorage.setItem('growerToken', JSON.stringify(this.growerToken));
      }

      // Hide modal
      this.hideAgreementModal();

      // Show success message
      this.showToast(result.message || 'Agreement signed successfully!', 'success');
    } catch (error) {
      this.showToast('Error signing agreement: ' + error.message, 'error');
    }
  }

  setupProductManagement() {
    this.productsContainer = document.getElementById('grower-products-list');

    this.fetchAndRenderProducts();
  }

  setupFilteringAndSorting() {
    // Get filter and sort elements
    this.growerFilter = document.getElementById('grower-filter');
    this.varietyFilter = document.getElementById('variety-filter');
    this.sortSelect = document.getElementById('sort-select');
    this.clearFiltersBtn = document.getElementById('clear-filters');
    this.productsSummary = document.getElementById('products-summary');

    // Add event listeners
    if (this.growerFilter) {
      this.growerFilter.addEventListener('input', () => this.applyFiltersAndSort());
    }
    if (this.varietyFilter) {
      this.varietyFilter.addEventListener('input', () => this.applyFiltersAndSort());
    }
    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', () => this.applyFiltersAndSort());
    }
    if (this.clearFiltersBtn) {
      this.clearFiltersBtn.addEventListener('click', () => this.clearAllFilters());
    }
  }

  clearAllFilters() {
    if (this.growerFilter) this.growerFilter.value = '';
    if (this.varietyFilter) this.varietyFilter.value = '';
    if (this.sortSelect) this.sortSelect.value = 'title-asc';
    this.applyFiltersAndSort();
  }

  applyFiltersAndSort() {
    if (!this.allProducts.length) return;

    // Apply filters
    this.filteredProducts = this.allProducts.filter(product => {
      const growerFilterValue = this.growerFilter?.value.toLowerCase() || '';
      const varietyFilterValue = this.varietyFilter?.value.toLowerCase() || '';
      
      const productTitle = (product.title || '').toLowerCase();
      const productVariety = (product.variety || product.dahlia_variety || '').toLowerCase();
      
      const matchesGrower = !growerFilterValue || productTitle.includes(growerFilterValue);
      const matchesVariety = !varietyFilterValue || productVariety.includes(varietyFilterValue);
      
      return matchesGrower && matchesVariety;
    });

    // Apply sorting
    const sortValue = this.sortSelect?.value || 'title-asc';
    this.sortProducts(sortValue);

    // Render filtered and sorted products
    this.renderFilteredProducts();
    this.updateProductsSummary();
  }

  sortProducts(sortValue) {
    this.filteredProducts.sort((a, b) => {
      switch (sortValue) {
        case 'title-asc':
          return (a.title || '').localeCompare(b.title || '');
        case 'title-desc':
          return (b.title || '').localeCompare(a.title || '');
        case 'variety-asc':
          const varietyA = a.variety || a.dahlia_variety || '';
          const varietyB = b.variety || b.dahlia_variety || '';
          return varietyA.localeCompare(varietyB);
        case 'variety-desc':
          const varietyA2 = a.variety || a.dahlia_variety || '';
          const varietyB2 = b.variety || b.dahlia_variety || '';
          return varietyB2.localeCompare(varietyA2);
        case 'quantity-asc':
          return (a.quantity || a.inventory_quantity || 0) - (b.quantity || b.inventory_quantity || 0);
        case 'quantity-desc':
          return (b.quantity || b.inventory_quantity || 0) - (a.quantity || a.inventory_quantity || 0);
        case 'date-asc':
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        case 'date-desc':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        default:
          return 0;
      }
    });
  }

  updateProductsSummary() {
    if (!this.productsSummary) return;
    
    const totalProducts = this.allProducts.length;
    const filteredCount = this.filteredProducts.length;
    
    if (filteredCount === totalProducts) {
      this.productsSummary.textContent = `Showing all ${totalProducts} products`;
    } else {
      this.productsSummary.textContent = `Showing ${filteredCount} of ${totalProducts} products`;
    }
  }

  async fetchAndRenderProducts() {
    if (!this.growerToken || !this.growerToken.token) return;
    
    try {
      const res = await fetch('https://growers-bk.vercel.app/api/products/?growerId=' + this.growerToken.grower.id, {
        headers: { Authorization: `Bearer ${this.growerToken.token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch products');
      const products = await res.json();
      
      // Store all products
      this.allProducts = products?.products || [];
      this.filteredProducts = [...this.allProducts];
      
      this.renderProducts(this.allProducts);
      this.updateProductsSummary();
    } catch (err) {
      this.productsContainer.innerHTML = `<div style="color:red;">Error loading products: ${err.message}</div>`;
    }
  }

  renderProducts(products) {
    if (!products || !products.length) {
      this.productsContainer.innerHTML = '<p>No products found. <a href="/pages/grower-add-product-form">Add your first product</a></p>';
      return;
    }
    
    this.renderFilteredProducts();
    
    // Save products for quick lookup
    this.productsById = {};
    products.forEach(p => { this.productsById[p._id] = p; });
  }

  renderFilteredProducts() {
    if (!this.filteredProducts || !this.filteredProducts.length) {
      this.productsContainer.innerHTML = '<p>No products match your filters. <a href="/pages/grower-add-product-form">Add a new product</a></p>';
      return;
    }
    
    this.productsContainer.innerHTML = `
      <table class="order-table">
        <thead>
          <tr>
            <th>Product Title</th>
            <th>Quantity</th>
            <th>Status</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          ${this.filteredProducts.map(product => `
            <tr>
              <td>${product.title || 'N/A'}</td>
              <td>${product.quantity || product.inventory_quantity || 'N/A'}</td>
              <td><span class="status-tag active">${product.status}</span></td>
              <td>${product.createdAt ? new Date(product.createdAt).toLocaleDateString() : 'N/A'}</td>
              <td>
                <button type="button" class="button-secondary" onclick="window.growerAccount.deleteProduct('${product._id}')" style="padding: 4px 8px; font-size: 12px; background-color: #dc3545; color: white;">Delete</button>
              </td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `;
  }

  editProduct(productId) {
    const product = this.productsById[productId];
    if (product) {
      // Redirect to edit form with product data
      const params = new URLSearchParams({
        id: productId,
        title: product.title || '',
        variety: product.variety || product.dahlia_variety || '',
        quantity: product.quantity || product.inventory_quantity || ''
      });
      window.location.href = `/pages/grower-add-product-form?${params.toString()}`;
    }
  }

  async deleteProduct(productId) {
    if (!confirm('Are you sure you want to delete this product? This action cannot be undone.')) {
      return;
    }

    try {
      const growerId = this.growerToken.grower.id || this.growerToken.grower._id;
      const res = await fetch(`https://growers-bk.vercel.app/api/products/${productId}?growerId=${growerId}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${this.growerToken.token}` 
        }
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to delete product');
      }

      // Refresh the products list
      this.fetchAndRenderProducts();
      
      // Show success message
      this.showToast('Product deleted successfully!', 'success');
    } catch (err) {
      this.showToast('Error deleting product: ' + err.message, 'error');
    }
  }

  showToast(message, type = 'info') {
    // Create toast notification
    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 12px 20px;
      border-radius: 4px;
      color: white;
      font-weight: 500;
      z-index: 10000;
      animation: slideIn 0.3s ease;
    `;
    
    // Set background color based on type
    switch (type) {
      case 'success':
        toast.style.backgroundColor = '#28a745';
        break;
      case 'error':
        toast.style.backgroundColor = '#dc3545';
        break;
      default:
        toast.style.backgroundColor = '#17a2b8';
    }
    
    toast.textContent = message;
    document.body.appendChild(toast);
    
    // Remove toast after 3 seconds
    setTimeout(() => {
      toast.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
      }, 300);
    }, 3000);
  }

  // Checklist Management
  async loadChecklists() {
    try {
      if (!this.growerToken || !this.growerToken.grower) {
        console.log('No grower token available');
        return;
      }

      const growerId = this.growerToken.grower.id;
      const response = await fetch(`https://growers-bk.vercel.app/api/checklists/growers/${growerId}/checklists`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.growerToken.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        this.renderChecklists(data.data);
        this.updateChecklistNotification(data.data);
      } else {
        console.error('Failed to load checklists:', data.error);
        this.renderEmptyChecklists();
      }
    } catch (error) {
      console.error('Error loading checklists:', error);
      this.renderEmptyChecklists();
    }
  }

  renderChecklists(checklists) {
    const container = document.getElementById('checklists-container');
    if (!container) return;

    if (!checklists || checklists.length === 0) {
      this.renderEmptyChecklists();
      return;
    }

    container.innerHTML = checklists
      .map(
        (checklist) => `
      <div class="checklist-item" data-checklist-id="${checklist._id}">
        <div class="checklist-header">
          <div>
            <h3 class="checklist-title">${checklist.title}</h3>
            <p class="checklist-description">${
              checklist.description || "No description provided"
            }</p>
          </div>
          <span class="checklist-status ${
            checklist.overallStatus
          }">${checklist.overallStatus.replace("_", " ")}</span>
        </div>
        
        <div class="checklist-meta">
          <span class="checklist-type">${checklist.type}</span>
          <span>Created: ${new Date(
            checklist.createdAt
          ).toLocaleDateString()}</span>
          ${
            checklist.completedAt
              ? `<span>Completed: ${new Date(
                  checklist.completedAt
                ).toLocaleDateString()}</span>`
              : ""
          }
        </div>

        ${
          checklist.items && checklist.items.length > 0
            ? `
          <div class="checklist-items">
            ${checklist.items
              .map(
                (item) => `
              <div class="checklist-item-detail">
                <input type="checkbox" class="checklist-item-checkbox" 
                       ${item.status === "completed" ? "checked" : ""} 
                       ${
                         checklist.overallStatus === "approved"
                           ? "disabled"
                           : ""
                       }
                       data-item-id="${item._id}">
                <span class="checklist-item-text">${item.title}</span>
                <span class="checklist-item-text">${item.description}</span>
              </div>
            `
              )
              .join("")}
          </div>
        `
            : ""
        }

        ${
          checklist.adminNotes
            ? `
          <div class="checklist-admin-notes">
            <div class="checklist-admin-notes-title">Admin Notes:</div>
            <p class="checklist-admin-notes-text">${checklist.adminNotes}</p>
          </div>
        `
            : ""
        }

        <div class="checklist-actions">
          ${
            checklist.overallStatus === "pending"
              ? `
            <button class="checklist-action-btn primary" onclick="growerAccount.updateChecklistStatus('${checklist._id}', 'in_progress')">
              Start Working
            </button>
          `
              : ""
          }
          ${
            checklist.overallStatus === "in_progress"
              ? `
            <button class="checklist-action-btn primary" onclick="growerAccount.updateChecklistStatus('${checklist._id}', 'completed')">
              Mark Complete
            </button>
          `
              : ""
          }
          ${
            checklist.overallStatus === "requires_attention"
              ? `
            <button class="checklist-action-btn primary" onclick="growerAccount.updateChecklistStatus('${checklist._id}', 'in_progress')">
              Continue Working
            </button>
          `
              : ""
          }
        </div>
      </div>
    `
      )
      .join("");

    // Set up refresh button
    const refreshBtn = document.getElementById('refresh-checklists');
    if (refreshBtn) {
      refreshBtn.onclick = () => this.loadChecklists();
    }
  }

  renderEmptyChecklists() {
    const container = document.getElementById('checklists-container');
    if (!container) return;

    container.innerHTML = `
      <div class="checklist-empty">
        <div class="checklist-empty-icon">📋</div>
        <p>No checklists available at the moment.</p>
      </div>
    `;

    // Set up refresh button
    const refreshBtn = document.getElementById('refresh-checklists');
    if (refreshBtn) {
      refreshBtn.onclick = () => this.loadChecklists();
    }
  }

  updateChecklistNotification(checklists) {
    const notification = document.getElementById('checklist-notification');
    if (!notification) return;

    // Check if there are any pending or requires_attention checklists
    const hasPendingChecklists = checklists.some(checklist => 
      checklist.overallStatus === 'pending' || 
      checklist.overallStatus === 'requires_attention' || 
      checklist.overallStatus === 'in_progress'
    );

    if (hasPendingChecklists) {
      notification.classList.remove('hidden');
    } else {
      notification.classList.add('hidden');
    }
  }

  async updateChecklistStatus(checklistId, newStatus) {
    try {
      if (!this.growerToken || !this.growerToken.grower) {
        console.log('No grower token available');
        return;
      }

      const growerId = this.growerToken.grower.id;
      const response = await fetch(`https://growers-bk.vercel.app/api/checklists/growers/${growerId}/checklists/${checklistId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${this.growerToken.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus,
          updatedBy: this.growerToken.grower._id
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        this.showToast(`Checklist ${newStatus.replace('_', ' ')} successfully!`, 'success');
        // Reload checklists to show updated status
        this.loadChecklists();
      } else {
        console.error('Failed to update checklist status:', data.error);
        this.showToast('Failed to update checklist status', 'error');
      }
    } catch (error) {
      console.error('Error updating checklist status:', error);
      this.showToast('Error updating checklist status', 'error');
    }
  }

  // Refresh profile data from API
  async refreshProfileData() {
    try {
      if (!this.growerToken || !this.growerToken.token) {
        console.log('No grower token available');
        return;
      }

      // Get updated profile data
      const profileData = await window.ProfileManager.getProfile(this.growerToken.token);
      
      if (profileData && profileData.grower) {
        // Update local token with new data
        this.growerToken.grower = { ...this.growerToken.grower, ...profileData.grower };
        
        // Update UI
        this.updateProfileInfo();
        this.updateDashboardInfo();
        this.updateWelcomeMessage();
        
        // Update sessionStorage
        sessionStorage.setItem('growerToken', JSON.stringify(this.growerToken));
      }
    } catch (error) {
      console.error('Error refreshing profile data:', error);
    }
  }

  // Show toast notification
  showToast(message, type = 'info') {
    // Create toast element if it doesn't exist
    let toast = document.getElementById('grower-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'grower-toast';
      toast.className = 'grower-toast';
      document.body.appendChild(toast);
    }

    // Set toast content and type
    toast.textContent = message;
    toast.className = `grower-toast ${type}`;
    
    // Show toast
    toast.classList.add('show');
    
    // Hide toast after 3 seconds
    setTimeout(() => {
      toast.classList.remove('show');
    }, 3000);
  }
}

// Initialize the growerAccount class
window.growerAccount = new GrowerAccount();

// Add toast styles
const toastStyles = document.createElement('style');
toastStyles.textContent = `
  .grower-toast {
    position: fixed;
    top: 20px;
    right: 20px;
    background: #333;
    color: white;
    padding: 12px 20px;
    border-radius: 4px;
    z-index: 10000;
    transform: translateX(100%);
    transition: transform 0.3s ease;
    max-width: 300px;
  }

  .grower-toast.show {
    transform: translateX(0);
  }

  .grower-toast.success {
    background: #4caf50;
  }

  .grower-toast.error {
    background: #f44336;
  }

  .grower-toast.info {
    background: #2196f3;
  }
`;
document.head.appendChild(toastStyles); 